import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { auth } from '../../lib/firebase';

/**
 * 関係者 (master / 担当地区管理者) のアカウントログイン hook (地区「強い停止」Phase 1)。
 *
 * 背景: user アプリの一般ユーザーは {@link useAnonymousAuth} で匿名サインインしている。地区が
 * 「強い停止」中のとき、一般ユーザーには「現在非公開中」画面しか出ないが、関係者は email/password で
 * ログインすれば custom claim (role) により通常フローに入れる必要がある。本 hook はその関係者ログインを
 * 担当する (admin アプリ `useAdminAuth` の user 版、最小機能)。
 *
 * 役割:
 *   - sign-in (email/password) + sign-out
 *   - 現在の auth state 配信 (user / role / districts)
 *   - getIdTokenResult(true) で custom claim を毎回最新化し role を解析 (rules 側で deny されるが UI guard 用)
 *
 * 匿名 ↔ アカウントの切替に関する注意:
 *   - `signInWithEmailAndPassword` は **現在の匿名ユーザーを置換** する (同じ Firebase Auth インスタンス
 *     `auth` を共有)。よってログイン成功で匿名 uid は失われる (関係者は匿名データを持たない前提)。
 *   - `signOut` 後は onAuthStateChanged が null を発火し、`useAnonymousAuth` が再び匿名サインインする。
 *   - `useAnonymousAuth` は無改変。本 hook は onAuthStateChanged を「読むだけ」(role 解析のみ) で、
 *     匿名サインインの発火は `useAnonymousAuth` に任せる (二重 signInAnonymously を避ける)。
 *
 * Phase 1 では viewer role は判定対象外 (role は 'master' / 'district' / null のみ)。
 */

export type AccountRole = 'master' | 'district' | null;

export type AccountAuthState =
  /** onAuthStateChanged の初回 callback 前。 */
  | { status: 'loading'; user: null; role: null; districts: string[] }
  /** 何らかの user (匿名 or アカウント) がサインイン中。role は claim に基づく (匿名は null)。 */
  | { status: 'authenticated'; user: User; role: AccountRole; districts: string[] }
  /** user 不在 (理論上は短時間。匿名再サインイン前の瞬間など)。 */
  | { status: 'unauthenticated'; user: null; role: null; districts: string[] };

interface TokenClaims {
  role?: string;
  districts?: string[];
}

const INITIAL_STATE: AccountAuthState = {
  status: 'loading',
  user: null,
  role: null,
  districts: [],
};

/**
 * Firebase ID トークンの custom claim から role / districts を取り出す (pure、unit test 可能)。
 * 匿名ユーザーは role claim を持たないため role=null / districts=[] になる。
 */
export function parseAccountClaims(
  claims: Record<string, unknown> | TokenClaims | null | undefined,
): {
  role: AccountRole;
  districts: string[];
} {
  const c = (claims ?? {}) as TokenClaims;
  const role: AccountRole =
    c.role === 'master' ? 'master' : c.role === 'district' ? 'district' : null;
  const districts = Array.isArray(c.districts)
    ? c.districts.filter((d): d is string => typeof d === 'string')
    : [];
  return { role, districts };
}

/**
 * email/password サインインのエラー code → 一般ユーザーに分かる日本語 (pure、unit test 可能)。
 * 内部識別子 (auth/xxx) は UI に出さない (UI: 内部 key 非表示ルール)。
 */
export function normalizeAccountSignInError(code: string | undefined): string {
  const c = code ?? '';
  if (
    c.includes('user-not-found') ||
    c.includes('wrong-password') ||
    c.includes('invalid-credential') ||
    c.includes('invalid-email')
  ) {
    return 'メールアドレスまたはパスワードが正しくありません。';
  }
  if (c.includes('too-many-requests')) {
    return '試行回数が多すぎます。しばらく経ってから再度お試しください。';
  }
  if (c.includes('network-request-failed')) {
    return 'ネットワークに接続できませんでした。通信環境をご確認ください。';
  }
  return 'ログインに失敗しました。時間をおいて再度お試しください。';
}

/**
 * Google サインインのエラー code → 一般ユーザーに分かる日本語 (pure、unit test 可能)。
 * popup を閉じた等の silent 系 (popup-closed-by-user / cancelled-popup-request) は呼び出し側で
 * 別途ハンドルし、本関数はメッセージ化のみ担当する。内部識別子 (auth/xxx) は UI に出さない。
 */
export function normalizeGoogleSignInError(code: string | undefined): string {
  const c = code ?? '';
  if (c.includes('unauthorized-domain')) {
    return 'このページではGoogleログインが許可されていません。管理者にご確認ください。';
  }
  if (c.includes('popup-blocked')) {
    return 'ポップアップがブロックされました。ポップアップを許可して再度お試しください。';
  }
  if (c.includes('operation-not-allowed')) {
    return 'Googleログインが有効になっていません。管理者にご確認ください。';
  }
  if (c.includes('account-exists-with-different-credential')) {
    return 'このメールアドレスは別のログイン方法で登録されています。';
  }
  if (c.includes('network-request-failed')) {
    return 'ネットワークに接続できませんでした。通信環境をご確認ください。';
  }
  return 'Googleログインに失敗しました。時間をおいて再度お試しください。';
}

export function useAccountAuth(): {
  state: AccountAuthState;
  /** email/password でサインイン (現在の匿名ユーザーを置換)。成功で role 反映済の user を返す。 */
  signIn: (email: string, password: string) => Promise<{ role: AccountRole; districts: string[] }>;
  /**
   * Google アカウントでサインイン (popup、現在の匿名ユーザーを置換)。成功で role 反映済を返す。
   * ユーザーが popup を閉じた / 二重起動した場合は **null** を返す (エラー表示しない)。
   * それ以外の失敗は日本語化した Error を throw する。
   */
  signInWithGoogle: () => Promise<{ role: AccountRole; districts: string[] } | null>;
  /** サインアウト (以降 useAnonymousAuth が匿名再サインイン)。 */
  signOutAccount: () => Promise<void>;
  error: Error | null;
} {
  const [state, setState] = useState<AccountAuthState>(INITIAL_STATE);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        // 匿名再サインインは useAnonymousAuth が担当する。ここでは role を読まず unauthenticated を配信。
        setState({ status: 'unauthenticated', user: null, role: null, districts: [] });
        return;
      }
      // role / districts は master が後から付与・変更しうるため、force=true で毎回最新化する
      // (admin useAdminAuth と同方針)。匿名ユーザーは role claim を持たず role=null になる。
      try {
        const tokenResult = await user.getIdTokenResult(true);
        const { role, districts } = parseAccountClaims(
          tokenResult.claims as Record<string, unknown>,
        );
        setState({ status: 'authenticated', user, role, districts });
      } catch {
        // claim 取得失敗時も user は確定しているので、role 不明 (= 一般扱い) で配信する。
        setState({ status: 'authenticated', user, role: null, districts: [] });
      }
    });
    return () => unsub();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      // sign-in 直後に最新 claim を取得して role を返す (onAuthStateChanged の非同期更新を待たずに
      // 呼び出し側 (AccountLoginPage) が即座に権限判定・地区再解決へ進めるようにする)。
      const tokenResult = await cred.user.getIdTokenResult(true);
      return parseAccountClaims(tokenResult.claims as Record<string, unknown>);
    } catch (e) {
      const raw = e as { code?: string; message?: string };
      const code = typeof raw.code === 'string' ? raw.code : '';
      const message = normalizeAccountSignInError(code);
      const err = new Error(message);
      (err as Error & { code?: string }).code = code;
      setError(err);
      throw err;
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const cred = await signInWithPopup(auth, provider);
      // sign-in 直後に最新 claim を取得して role を返す (email/password と同方針)。
      const tokenResult = await cred.user.getIdTokenResult(true);
      return parseAccountClaims(tokenResult.claims as Record<string, unknown>);
    } catch (e) {
      const raw = e as { code?: string; message?: string };
      const code = typeof raw.code === 'string' ? raw.code : '';
      // popup を閉じた / 二重起動した場合は silent (エラー表示しない、呼び出し側は何もしない)。
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return null;
      }
      const message = normalizeGoogleSignInError(code);
      const err = new Error(message);
      (err as Error & { code?: string }).code = code;
      setError(err);
      throw err;
    }
  }, []);

  const signOutAccount = useCallback(async () => {
    setError(null);
    await signOut(auth);
  }, []);

  return { state, signIn, signInWithGoogle, signOutAccount, error };
}
